import _ from 'lodash';
import moment from 'moment-timezone';
import { sqDurationStore, sqTrendConditionStore, sqTrendSeriesStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore, type ToolItem } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_BOOTSTRAP,
  DEFAULT_CONTAMINATION,
  DEFAULT_NUM_ESTIMATORS,
  INPUT_NAMES,
} from '@/tools/isolationForestV2/isolationForest.constants';
import type { BaseToolPersistedState, ModelRehydratePayload, ToolParameter } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { Capsule } from '@/utilities/datetime.constants';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

interface IsolationForestStateV2 extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any; // any because of Baobab's `monkey()`
  modelPropertiesExpanded: boolean;
  color: string;
  numEstimators: number;
  contaminationAuto: boolean;
  contamination: number;
  bootstrap: boolean;
  cleanCapsules: boolean;
  capsuleMinDuration: ValueWithUnitsItem;
  capsuleMergeDuration: ValueWithUnitsItem;
  inputSignals?: ToolItem[] | null;
  condition?: ToolItem | null;
  restrictToCondition?: ToolItem | null;
}

export type IsolationForestDehydratedStateV2 = Omit<IsolationForestStateV2, 'window'>;

export class IsolationForestStoreV2 extends BaseToolStore {
  static readonly storeName = 'sqIsolationForestStoreV2';
  type = TREND_TOOLS.ISOLATION_FOREST_V_2;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    inputSignals: {
      predicate: (parameter: ToolParameter) => parameter?.name?.startsWith('signal'),
      multiple: true,
    },
  };

  initialize() {
    const initialState: IsolationForestStateV2 = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      numEstimators: DEFAULT_NUM_ESTIMATORS,
      contaminationAuto: true,
      contamination: DEFAULT_CONTAMINATION,
      bootstrap: DEFAULT_BOOTSTRAP,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] {
    return this.state.get('inputSignals');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window() {
    return this.state.get('window');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get numEstimators(): number {
    return this.state.get('numEstimators');
  }

  get contaminationAuto(): boolean {
    return this.state.get('contaminationAuto');
  }

  get contamination(): number {
    return this.state.get('contamination');
  }

  get bootstrap(): boolean {
    return this.state.get('bootstrap');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration() {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration() {
    return this.state.get('capsuleMergeDuration');
  }

  dehydrate(): IsolationForestDehydratedStateV2 {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<IsolationForestDehydratedStateV2>) {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setNumEstimators(payload: { numEstimators: number }): void {
    this.state.set('numEstimators', payload.numEstimators);
  }

  setContaminationAuto(payload: { contaminationAuto: boolean }): void {
    this.state.set('contaminationAuto', payload.contaminationAuto);
  }

  setContamination(payload: { contamination: number }): void {
    this.state.set('contamination', payload.contamination);
  }

  setBootstrap(payload: { bootstrap: boolean }): void {
    this.state.set('bootstrap', payload.bootstrap);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }): void {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  modifyConfigParams(config: any) {
    return _.omit(config, ['modelPropertiesExpanded']);
  }

  modelToolRehydrateForEdit = (payload: ModelRehydratePayload): void => {
    if (payload.type !== this.type) {
      return;
    }
    this.initialize();
    this.state.set('id', payload.instanceId);
    (payload.modelData?.inputFeatures ?? []).forEach((feature: any): void => {
      const inputName: string = feature.name;
      switch (inputName) {
        case SeeqNames.Models.InstanceNameInputFeatureName:
          // TODO: CRAB-53864: Handle duplicate names more gracefully
          this.state.set('name', payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`);
          break;
        case INPUT_NAMES.numEstimatorsInput:
          this.state.set('numEstimators', feature.inputNumeric ?? DEFAULT_NUM_ESTIMATORS);
          break;
        case INPUT_NAMES.contaminationInput:
          this.state.set('contamination', feature.inputNumeric ?? DEFAULT_CONTAMINATION);
          this.state.set('contaminationAuto', !feature.inputNumeric);
          break;
        case INPUT_NAMES.bootstrapInput:
          this.state.set('bootstrap', feature.inputBoolean ?? DEFAULT_BOOTSTRAP);
          break;
        case INPUT_NAMES.startTimeInput: {
          const startTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowStart', moment(startTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.endTimeInput: {
          const endTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowEnd', moment(endTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.mergeCapsuleDurationInput: {
          this.state.set('cleanCapsules', !_.isNil(feature.inputNumeric));
          const mergeDuration: ValueWithUnitsItem = {
            value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
            units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
          };
          this.state.set('capsuleMergeDuration', mergeDuration);
          break;
        }
        case INPUT_NAMES.minCapsuleDurationInput: {
          this.state.set('cleanCapsules', !_.isNil(feature.inputNumeric));
          const mergeDuration: ValueWithUnitsItem = {
            value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
            units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
          };
          this.state.set('capsuleMinDuration', mergeDuration);
          break;
        }
        case INPUT_NAMES.trainWithinConditionInput:
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('condition', _.pick(item, ['id', 'name']));
          }
          break;

        case INPUT_NAMES.restrictOutputToConditionInput: {
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('restrictToCondition', _.pick(item, ['id', 'name']));
          }
          break;
        }
        default:
          if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
            const existingSignals = this.state.get('inputSignals') || [];
            const item = sqTrendSeriesStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('inputSignals', [...existingSignals, _.pick(item, ['id', 'name'])]);
          }
          break;
      }
    });
  };

  protected readonly handlers = {
    ...this.baseHandlers,
    IF_SET_TRAINING_WINDOW: this.setTrainingWindow,
    IF_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    IF_SET_NUM_ESTIMATORS: this.setNumEstimators,
    IF_SET_CONTAMINATION_AUTO: this.setContaminationAuto,
    IF_SET_CONTAMINATION: this.setContamination,
    IF_SET_BOOTSTRAP: this.setBootstrap,
    IF_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    IF_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    IF_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
    MODEL_TOOL_REHYDRATE_FOR_EDIT: this.modelToolRehydrateForEdit,
  };
}
